import { z } from 'zod';
import type { InteractionBaseDto } from './interactions/interactionBase.dto';
import type { UserDto } from './user.dto';
import { MessageType, StageKey } from '../../../definitions';

/** Texts-table filter: unassigned messages, or inbound texts to a guide resource number. */
export const SmsTextTypeSchema = z.enum(['unassigned', 'resource']);

/** Query value for GET /sms `textType`. */
export type SmsTextType = z.infer<typeof SmsTextTypeSchema>;

export interface PaginatedSMSMessagesDto {
  smsMessages: SMSMessageDto[];
  count: number;
  users: UserDto[];
}

export interface GetPaginatedSMSMessagesRequest {
  subjectIds?: string[];
  onlyUnassignedMessages?: boolean;
  showOnlyMyTexts?: boolean;
  textType?: SmsTextType;
  directionType?: 'incoming' | 'outgoing';
  searchTerm?: string;
  searchTermType?: 'string' | 'subjectId' | 'number';
  'page-start'?: number;
  'page-size'?: number;
}

export type SMSMessageDto = {
  id: number;
  text: string;
  external: boolean;
  /** True when this inbound text arrived on a guide resource number. */
  isResourceSms: boolean;
  /** True when a guide dismissed the Assign text docket row. */
  noFollowUpNeeded: boolean;
  status: string;
  numSegments: number;
  from: string;
  to: string;
  fileIds: string[];
  errorCode: string;
  message?: MessageDto;
  createdAt: number;
};

export interface AssignSMSMessageRequestDto {
  subjectId: string;
}

export type MessageDto = {
  id: number;
  type: MessageType;
  incoming: boolean;
  text: string;
  smsId?: number;
  sendAt: number;
  sentAt: number;
  smsStatus?: string;
  smsNumSegments?: number;
  smsErrorCode?: number;
  smsPhoneNumber?: string;
  subjectId: string;
  userId: string;
  userAssignedGuideId: string;
  feedId: number;
  fileIds: string[];
  noFollowUpNeeded: boolean;
  scheduledEventId: number;
  createdAt: number;
  updatedAt?: number;
  phoneNumberId?: number;
  wasSentByCronJob: boolean;
  sms: SMSMessageDto;
} & InteractionBaseDto<'message'>;

export interface MessageTemplateCategoryAdminDto {
  id: number;
  name: string;
  sortOrder: number;
  stageKey: StageKey;
}

export interface MessageTemplateClientDto {
  clientId: number;
  templateId: number;
  id: number;
  createdAt?: number;
  updatedAt?: number;
  deletedAt?: number;
}

export interface MessageTemplateDto {
  id: number;
  template: string;
  categoryId: number;
  sortOrder: number;
  template_clients: MessageTemplateClientDto[];
  allClients: boolean;
}

export interface MessageTemplateAdminDto {
  id?: number;
  template: string;
  categoryId: number;
  sortOrder: number;
  template_clients: number[];
  allClients: boolean;
}

export interface MessageTemplateCategoryDto {
  id: number;
  name: string;
  templates: MessageTemplateDto[];
  sortOrder: number;
  stageKey: StageKey;
}

export interface TemplateCategoryDto {
  id: number;
  name: string;
  templates: TemplateCategoryContentDto[];
  sortOrder: number;
}

export type TemplateCategoryContentDto = {
  id: number;
  [key: string]: unknown;
  client_ids: string;
  sort_order: number;
};

export interface MessageTemplateEventDto {
  action: string;
  template: string;
}
export interface CreateMessageRequestDto {
  text: string;
  fileIds: string[];
  subjectId: string;
  sendAt?: number;
  scheduledEventId?: number;
  phoneNumberId?: number;
}

export interface UpdateMessageRequestDto {
  text: string;
  fileIds: string[];
  sendAt?: number;
  phoneNumberId?: number;
}

export const UpdateMessageTemplateCategorySchema = z.object({
  name: z.string().optional(),
  sortOrder: z.coerce.number().optional(),
  stageKey: z.nativeEnum(StageKey).optional(),
});

export type UpdateMessageTemplateCategoryDto = z.infer<
  typeof UpdateMessageTemplateCategorySchema
>;

export const CreateMessageTemplateCategorySchema = z
  .object({
    name: z.string().nonempty('required'),
    sortOrder: z.coerce.number().min(1),
    stageKey: z.nativeEnum(StageKey),
  })
  .strict();

export type CreateMessageTemplateCategoryDto = z.infer<
  typeof CreateMessageTemplateCategorySchema
> & {
  id?: number;
};

export const MAX_TEXT_MESSAGE_LENGTH = 1600;
export const MessageFormSchema = z
  .object({
    phoneNumberId: z.number(),
    message: z
      .string()
      .nonempty('Required')
      .max(
        MAX_TEXT_MESSAGE_LENGTH,
        `Message must be less than ${MAX_TEXT_MESSAGE_LENGTH} characters`
      ),
  })
  .strict();

export type MessageForm = z.infer<typeof MessageFormSchema>;
